import { createFileRoute } from '@tanstack/react-router';
import { Heading } from '@osd/ui/heading';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /icons/getting-started — install + import + API primer for `@osd/icons`.
 *
 * Mirrors `/ui/getting-started` in shape. Three install shapes (workspace,
 * browser pack, Vite / npm tarball), per-icon subpath import discipline
 * (root/osd#20 — no barrels), and the full prop surface documented inline.
 *
 * Per root/osd#80 + root/osd#81.
 */
export const Route = createFileRoute('/icons/getting-started')({
  component: IconsGettingStarted,
});

function IconsGettingStarted() {
  return (
    <Stack gap={6}>
      <Stack gap={2}>
        <Heading level={1}>Icons — getting started</Heading>
        <Text muted>
          <code>@osd/icons</code> is the OSD-native icon set: hand-authored SVG glyphs as React
          components, zero runtime dependencies, per-icon subpath exports for tree-shaking. 215
          icons across 12 categories (UI controls, arrows, files &amp; folders, communication, user
          & account, status &amp; feedback, media &amp; devices, editing & text, navigation &amp;
          layout, weather, time &amp; calendar, misc utility).
        </Text>
      </Stack>

      <Section title="Install" hint="Three consumer shapes; pick the one that matches your build.">
        <Stack gap={4}>
          <CodeBlock
            title="1. Workspace consumer (recommended)"
            language="tsx"
            code={`# In apps/<your-app>/package.json
"dependencies": {
  "@osd/icons": "workspace:*"
}

# Then in your component:
import { ChevronRight } from '@osd/icons/chevron-right';
<ChevronRight aria-label="next" />`}
          />
          <CodeBlock
            title="2. Vite / esbuild / npm consumer (tarball)"
            language="bash"
            code={`# Install directly from the served tarball — npm fetches it over
# HTTPS and unpacks into node_modules.
npm install https://bugfix.pro/_icons/_tgz.tgz

# Then subpath-import each icon (no barrel re-export — see §Tree-shaking).
import { Check } from '@osd/icons/check';
import { Search } from '@osd/icons/search';`}
          />
          <CodeBlock
            title="3. Browser-pack consumer (no build)"
            language="html"
            code={`<!-- Drop the served bundle + import map. Useful for prototypes
     and third-party demos that don't run a bundler. -->
<script type="importmap">
{
  "imports": {
    "@osd/icons/check":             "https://bugfix.pro/_icons/v0/icons/os-check.js",
    "@osd/icons/chevron-right":     "https://bugfix.pro/_icons/v0/icons/os-chevron-right.js"
  }
}
</script>
<script type="module">
  import { Check } from '@osd/icons/check';
  document.body.appendChild(document.createElement('div')).append(
    Object.assign(document.createElement('div'), { id: 'ok' })
  );
</script>`}
          />
        </Stack>
      </Section>

      <Section title="API" hint="every icon ships the same component shape">
        <Stack gap={4}>
          <Text>
            Every icon is a <code>forwardRef&lt;SVGSVGElement, IconProps&gt;</code> wrapper around a
            shared <code>IconBase</code> SVG element. The props surface:
          </Text>
          <CodeBlock
            language="tsx"
            code={`interface IconProps
  extends Omit<SVGProps<SVGSVGElement>, 'children' | 'color'> {
  /** Pixel size for width + height. Default: 24 */
  size?: number | string;
  /** Stroke / fill color. Default: 'currentColor' */
  color?: string;
  /** Stroke width for stroke-based icons. Default: 1.5 */
  strokeWidth?: number;
}`}
          />
          <Text>
            Defaults are baked in: 24×24 viewBox, 1.5 stroke-width, round caps and joins,{' '}
            <code>currentColor</code> stroke, <code>aria-hidden="true"</code> by default (pass{' '}
            <code>aria-label</code> to flip the role to <code>"img"</code>).
          </Text>
        </Stack>
      </Section>

      <Section title="Tree-shaking" hint="why subpath imports matter">
        <Stack gap={3}>
          <Text>
            The package is published with <code>"sideEffects": false</code> and a per-icon subpath
            in <code>package.json</code>'s <code>exports</code> map. Always import via the subpath:
          </Text>
          <CodeBlock
            language="tsx"
            code={`// ✅ Tree-shakable — bundle contains exactly this icon.
import { ChevronRight } from '@osd/icons/chevron-right';

// ❌ Loads the full barrel — kills tree-shaking.
import { ChevronRight } from '@osd/icons';`}
          />
        </Stack>
      </Section>

      <Section
        title="Relationship to UI Kit"
        hint="@osd/icons and @osd/ui are sibling products, not parent/child"
      >
        <Stack gap={3}>
          <Text>
            <code>@osd/icons</code> ships the glyphs; <code>@osd/ui</code> ships the primitives that
            use them (Button, Modal, Toast, etc.). Icons are styled by inheriting{' '}
            <code>currentColor</code>, so they automatically pick up the host context's text color —
            which means they pick up the <code>@osd/ui</code> design tokens (
            <code>--osd-color-fg</code>, <code>--osd-color-fg-muted</code>, etc.) for free.
          </Text>
          <Text>
            The two packages share a build, test, and deploy pipeline but no runtime code. You can
            use <code>@osd/icons</code> without <code>@osd/ui</code> and vice versa.
          </Text>
        </Stack>
      </Section>
    </Stack>
  );
}
